import gql from "graphql-tag";

import {gqlOp, type GqlOperation} from "@khan/data-gql-core";

import type {
    clearBrandNewNotifications as GqlClearBrandNewNotificationsResponse,
    clearBrandNewNotificationsVariables as GqlClearBrandNewNotificationsVariables,
} from "./__graphql-types__/clearBrandNewNotifications.ts";
import type {
    getNotificationsForUser as GqlNotificationsForUserResponse,
    getNotificationsForUserVariables as GqlNotificationsForUserVariables,
} from "./__graphql-types__/getNotificationsForUser.ts";
import type {
    readNotification as GqlReadNotification,
    readNotificationVariables as GqlReadNotificationVariables,
} from "./__graphql-types__/readNotification.ts";

export const CLEAR_NOTIFICATIONS_MUTATION: GqlOperation<
    GqlClearBrandNewNotificationsResponse,
    GqlClearBrandNewNotificationsVariables
> = gqlOp(gql`
    mutation clearBrandNewNotifications {
        clearBrandNewNotifications {
            error {
                code
            }
        }
    }
`);

export const READ_NOTIFICATION_MUTATION: GqlOperation<
    GqlReadNotification,
    GqlReadNotificationVariables
> = gqlOp(gql`
    mutation readNotification($key: String!) {
        readNotification(key: $key) {
            error {
                code
            }
        }
    }
`);

export const NOTIFICATIONS_FOR_USER_QUERY: GqlOperation<
    GqlNotificationsForUserResponse,
    GqlNotificationsForUserVariables
> = gqlOp(gql`
    query getNotificationsForUser($after: ID) {
        user {
            id
            notifications(after: $after) {
                notifications {
                    __typename
                    brandNew
                    class_
                    date
                    kaid
                    read
                    url
                    urlsafeKey
                    ...ThreadCreatedNotificationType
                    ...AssignmentDueDateNotificationType
                    ...AssignmentCreatedNotificationType
                    ...CoachRequestNotificationType
                    ...BadgeNotificationType
                    ...CourseMasteryGoalCreatedNotificationType
                    ...ModeratorNotificationType
                    ...ProgramFeedbackNotificationType
                    ...CoachRequestAcceptedNotificationType
                    ...AvatarNotificationType
                    ...InfoNotificationType
                    ...ResponseFeedbackNotificationType
                    ...GroupedBadgeNotificationType
                    ...UnitMasteryGoalCreatedNotificationType
                    ...UnitMasteryDueDateCreatedNotificationType
                    ...CourseMasteryDueDateCreatedNotificationType
                    ...MasteryGoalDueDateApproachingCreatedNotificationType
                }
                pageInfo {
                    nextCursor
                }
            }
        }
    }
    fragment ThreadCreatedNotificationType on ThreadCreatedNotification {
        coachee {
            id
            kaid
            nickname
        }
        threadId
        flagged
    }
    fragment AssignmentDueDateNotificationType on AssignmentDueDateNotification {
        numAssignments
        dueDate
        contentTitle
        curationNodeIconURL
    }
    fragment AssignmentCreatedNotificationType on AssignmentCreatedNotification {
        numAssignments
        contentTitle
        curationNodeIconURL
        className
    }
    fragment CoachRequestNotificationType on CoachRequestNotification {
        coachIsParent
        coach {
            id
            kaid
            nickname
        }
    }
    fragment BadgeNotificationType on BadgeNotification {
        badgeName
        badge {
            description
            fullDescription
            name
            relativeUrl
            icons {
                compactUrl
            }
        }
    }
    fragment CourseMasteryGoalCreatedNotificationType on CourseMasteryGoalCreatedNotification {
        curationNodeIconURL
        curationNodeTranslatedTitle
        masteryPercentage
    }
    fragment ModeratorNotificationType on ModeratorNotification {
        text
    }
    fragment ProgramFeedbackNotificationType on ProgramFeedbackNotification {
        authorAvatarSrc
        authorNickname
        feedbackType
        translatedScratchpadTitle
        content
    }
    fragment CoachRequestAcceptedNotificationType on CoachRequestAcceptedNotification {
        isMultipleClassrooms
        student {
            id
            email
            nickname
        }
        classroom {
            cacheId
            id
            name
            topics {
                id
                slug
                iconUrl
                key
                translatedStandaloneTitle
            }
        }
    }
    fragment AvatarNotificationType on AvatarNotification {
        name
        thumbnailSrc
    }
    fragment InfoNotificationType on InfoNotification {
        notificationType
    }
    fragment ResponseFeedbackNotificationType on ResponseFeedbackNotification {
        authorAvatarUrl
        authorNickname
        feedbackType
        focusTranslatedTitle
        content
        sumVotesIncremented
    }
    fragment GroupedBadgeNotificationType on GroupedBadgeNotification {
        badgeNotifications {
            badge {
                badgeCategory
                description
                fullDescription
                name
                icons {
                    compactUrl
                }
            }
        }
    }
    fragment UnitMasteryGoalCreatedNotificationType on UnitMasteryGoalCreatedNotification {
        # Alias the numAssignments field to numAssignmentsCount to avoid a name
        # collision with the numAssignments field in the AssignmentDueDateNotification.
        numAssignmentsCount: numAssignments
        # Alias the classroom field to classroomInfo to avoid a name collision
        # with the classroom field in the CoachRequestAcceptedNotification.
        classroomInfo: classroom {
            cacheId
            id
            coach {
                id
                nickname
            }
        }
        unit {
            id
            iconUrl
            parent {
                id
                iconUrl
            }
        }
    }
    fragment UnitMasteryDueDateCreatedNotificationType on UnitMasteryDueDateCreatedNotification {
        dueDate
        unit {
            id
            iconUrl
            translatedStandaloneTitle
        }
    }
    fragment CourseMasteryDueDateCreatedNotificationType on CourseMasteryDueDateCreatedNotification {
        dueDate
        course {
            id
            iconUrl
            translatedStandaloneTitle
        }
    }
    fragment MasteryGoalDueDateApproachingCreatedNotificationType on MasteryGoalDueDateApproachingCreatedNotification {
        classroomInfo: classroom {
            id
            cacheId
        }
    }
`);
